import { Component, OnInit, Output, EventEmitter, Input, forwardRef, OnChanges, SimpleChanges, Inject, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Observable, of, Subscription, catchError, filter, map } from 'rxjs';
import { differenceBy, findIndex } from 'lodash';

import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';

import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { Theme } from '../../../../Social/Intelligence/models/theme.model';

import { TagGroupsService } from '../../../Tags/services/tag-groups.service';
import { TagsService } from '../../../Tags/services/tags.service';
import { TagGroupModel } from '../../../Tags/models/tag-group.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { GenericStyleModel } from '../../../Styles/models/generic-style.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';

import { SeriesCompareOptionsEnum } from '../../enums/series-compare-options.enum';
import { SeriesTypeEnum } from '../../enums/series-type.enum';
import { ReportSeriesModel } from '../../models/report-series.model';
import { SeriesPickerCompareFieldModel } from '../../models/series-picker-compare-field.model';
import { ReportsSeriesFilterService } from '../../services/reports-series-filters.service';
import { SeriesPickerFieldComponent } from '../SeriesPickerField/series-picker-field.component';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';

@Component({
    selector: 'agpr-series-picker-compare-field',
    templateUrl: './series-picker-compare-field.component.html',
    styleUrls: ['./series-picker-compare-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => SeriesPickerFieldComponent)
        },
        ReportsSeriesFilterService,
        {
            provide: FilterService,
            useExisting: ReportsSeriesFilterService
        }
    ],
    standalone: false
})
export class SeriesPickerCompareFieldComponent extends AbstractFormFieldComponent implements OnInit, OnChanges, OnDestroy {
    readonly messageSeverityEnum = messageSeverityEnum;
    defaultColors: string[] = [];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    readonly SeriesCompareOptionsEnum = SeriesCompareOptionsEnum;
    selectedCompareOption: DropdownOptionModel = new DropdownOptionModel('Reports.seriesCompareOption.individual', SeriesCompareOptionsEnum.individual);

    compareOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Reports.seriesCompareOption.individual', SeriesCompareOptionsEnum.individual),
        new DropdownOptionModel('Reports.seriesCompareOption.tagsWithinTagGroup', SeriesCompareOptionsEnum.tagsWithinTagGroup),
        new DropdownOptionModel('Reports.seriesCompareOption.socialTopicsWithinTheme', SeriesCompareOptionsEnum.socialTopicsWithinTheme)
    ];

    selectedParentOption: DropdownOptionModel;
    selectedCharts: string[];

    tagGroups: TagGroupModel[];
    parentOptions: DropdownOptionModel[];
    parentOptions$: Observable<DropdownOptionModel[]>;

    socialThemes: Theme[];

    parentPlaceholders = {};

    emptyParent: boolean = false;

    seriesList: ReportSeriesModel[];
    cachedSeriesList: ReportSeriesModel[] = null;

    hasActiveFilters: boolean = false;

    @Input() declare field: SeriesPickerCompareFieldModel;
    @Input() value: ReportSeriesModel[] = this.getDefaultSeriesList();
    @Input() showContent: boolean = true;
    @Input() isNewReport: boolean = false;
    @Input() ariaDescribedBy: string;
    @Input() showHiddenOption: boolean = true;

    @Output() changes: EventEmitter<ReportSeriesModel[]> = new EventEmitter<ReportSeriesModel[]>();

    private themeAgentMap = new Map<number, MediaSubscription[]>();
    private subscriptions: Subscription[] = [];

    constructor(
        private tagGroupsService: TagGroupsService,
        private tagsService: TagsService,
        private filterService: ReportsSeriesFilterService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private themeService: ThemeService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        super();
    }

    ngOnInit() {
        this.parentPlaceholders[SeriesCompareOptionsEnum.tagsWithinTagGroup] = 'Reports.series.type.tagGroup.placeholder';
        this.parentPlaceholders[SeriesCompareOptionsEnum.socialTopicsWithinTheme] = 'Reports.series.type.theme.placeholder';

        this.subscriptions.push(
            this.tagGroupsService.getTagGroupsList().subscribe(
                tagGroupsList => {
                    if (!tagGroupsList) {
                        return;
                    }

                    this.tagGroups = tagGroupsList;
                }
            ),
            this.themeService.all$.subscribe(socialThemesList => {
                if (!socialThemesList) {
                    return;
                }

                this.socialThemes = socialThemesList;
            }),
            this.themeService.themeAgentMap$.subscribe(themeAgentMap => {
                this.themeAgentMap = themeAgentMap;
            }),
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors.generalColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors))
            ).subscribe(generalColors => {
                this.defaultColors = Object.values(generalColors);
                const accentColor = this.block.displayElement?.styles?.colorMap?.['accentColor'];
                if (accentColor) {
                    this.defaultColors[0] = accentColor;
                }
            }),

            this.filterService.getSearchFilters().subscribe(
                searchCriteria => {
                    if (!(this.seriesList || searchCriteria)) {
                        return;

                        // these filters are only for applying non individual
                    } else if (this.selectedCompareOption.value === SeriesCompareOptionsEnum.individual) {
                        return;
                    }

                    this.seriesList.forEach(series => {
                        if (!series.mentions.filters) {
                            series.mentions.filters = new MentionSearchModel();
                            series.mentions.filters.searchCriteria = searchCriteria;
                        }

                        series.mentions.filters.searchCriteria = searchCriteria;
                    });

                    this.changes.emit(this.seriesList.slice());
                }
            ),

            this.filterService.getFilters().subscribe(filters => {
                this.hasActiveFilters = !!filters.find(filterChip => filterChip.chips && filterChip.chips.length > 0);
            }),

            this.currentUserStateService.getPermissions().pipe(
                filter(x => !!x)
            ).subscribe((permissions) => {
                // hide monitoring compareOptions if you don't have access
                if (!permissions.includes(userPermissionsEnum.advancedMonitoring)) {
                    this.compareOptions = differenceBy(this.compareOptions, [{value: SeriesCompareOptionsEnum.tagsWithinTagGroup}], 'value');
                }
                // hide social compareOptions if you don't have access
                if (!permissions.includes(userPermissionsEnum.socialIntelligence)) {
                    this.compareOptions = differenceBy(this.compareOptions, [{value: SeriesCompareOptionsEnum.socialTopicsWithinTheme}], 'value');
                }

                this.initSelectedCompareOption();
            })
        );

        this.selectedCharts = this.field.tooltipMessage;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            if (this.value && this.value.length > 0) {
                this.seriesList = this.value;
            } else {
                this.seriesList = this.getDefaultSeriesList();
            }
        }
    }

    /**
     * Returns a new ReportSeriesModel[] with default values
     *
     * @returns ReportSeriesModel[]
     */
    getDefaultSeriesList(): ReportSeriesModel[] {
        let blockTypeKey = null;

        if (this.block && this.block.type && this.block.type.key) {
            blockTypeKey = this.block.type.key;
        }

        const series = new ReportSeriesModel(null, blockTypeKey);
        series.color = this.defaultColors[0];

        return [series];
    }

    /**
     * Checks if the series loaded are being used to compare tags within a tag group,
     * and sets the appropriate selected dropdown options.
     */
    async initSelectedCompareOption() {
        if (!this.seriesList || !this.seriesList[0]) {
            return;
        }

        if (this.seriesList[0].styles && this.seriesList[0].styles.parentGroupId) {
            const parentGroupId = this.seriesList[0].styles.parentGroupId as number;
            const tagGroupById = await this.tagGroupsService.getTagGroupModelById(parentGroupId);

            this.selectedParentOption = new DropdownOptionModel(tagGroupById.name, tagGroupById);
            this.selectedCompareOption = this.compareOptions.find(option => option.value === SeriesCompareOptionsEnum.tagsWithinTagGroup);

            if (this.seriesList[0].mentions.filters) {
                this.filterService.getFiltersFromSearch(this.seriesList[0].mentions.filters.searchCriteria);
            } else {
                this.filterService.clearFilters();
            }

            this.filterService.resetFilters(SeriesTypeEnum.tag);

            this.parentOptions = this.tagGroups.map(tagGroup => {
                return new DropdownOptionModel(tagGroup.name, tagGroup);
            });
            this.parentOptions$ = of(this.parentOptions);

        } else if (this.seriesList[0].styles && this.seriesList[0].styles.parentThemeId) {
            const parentThemeId = this.seriesList[0].styles.parentThemeId as number;

            const themeIndex = findIndex(this.socialThemes, { id: parentThemeId });
            if (themeIndex >= 0) {
                const theme = this.socialThemes[themeIndex];

                this.selectedParentOption = new DropdownOptionModel(theme.name, theme);
                this.selectedCompareOption = this.compareOptions.find(option => option.value === SeriesCompareOptionsEnum.socialTopicsWithinTheme);

                if (this.seriesList[0].mentions.filters) {
                    this.filterService.getFiltersFromSearch(this.seriesList[0].mentions.filters.searchCriteria);
                } else {
                    this.filterService.clearFilters();
                }

                this.filterService.resetFilters(SeriesTypeEnum.theme);

                this.parentOptions = this.socialThemes.map(tagGroup => {
                    return new DropdownOptionModel(tagGroup.name, tagGroup);
                });
                this.parentOptions$ = of(this.parentOptions);
            }
        } else {
            this.selectedCompareOption = this.compareOptions[0];
        }
    }

    selectCompareOption(): void {
        switch (this.selectedCompareOption.value) {
            case SeriesCompareOptionsEnum.tagsWithinTagGroup:
                this.parentOptions = this.tagGroups.map(tagGroup => {
                    return new DropdownOptionModel(tagGroup.name, tagGroup);
                });
                this.parentOptions$ = of(this.parentOptions);

                // cache work, so user doesn't lose all their multi-series stuff if they forgot to save and selected this accidentally
                this.cachedSeriesList = this.seriesList;
                this.seriesList = this.getDefaultSeriesList();
                this.filterService.resetFilters(SeriesTypeEnum.tag);

                if (this.selectedParentOption?.value instanceof Theme) {
                    this.selectedParentOption = null;
                }
                break;
            case SeriesCompareOptionsEnum.socialTopicsWithinTheme:

                this.parentOptions = this.socialThemes.map(tagGroup => {
                    return new DropdownOptionModel(tagGroup.name, tagGroup);
                });
                this.parentOptions$ = of(this.parentOptions);

                // cache work, so user doesn't lose all their multi-series stuff if they forgot to save and selected this accidentally
                this.cachedSeriesList = this.seriesList;
                this.seriesList = this.getDefaultSeriesList();
                this.filterService.resetFilters(SeriesTypeEnum.theme);

                if (this.selectedParentOption?.value instanceof TagGroupModel) {
                    this.selectedParentOption = null;
                }
                break;

            case SeriesCompareOptionsEnum.individual:
                this.selectParentOption(null);

                if (this.cachedSeriesList) {
                    this.seriesList = this.cachedSeriesList;
                }

                this.seriesList.forEach(series => {
                    if (series.styles) {
                        delete series.styles.parentGroupId;
                        delete series.styles.parentThemeId;
                    }
                });

                this.emptyParent = false;
                break;
        }

        this.filterService.clearFilters();
        this.changes.emit(this.seriesList.slice());
    }

    selectParentOption(option: DropdownOptionModel): void {
        if (option) {
            switch (this.selectedCompareOption.value) {
                case SeriesCompareOptionsEnum.tagsWithinTagGroup:
                    if (option.value.tagIds.length === 0) {
                        this.emptyParent = true;
                        return;
                    }

                    this.buildTagGroupComparisonSeriesList(option.value as TagGroupModel);
                    break;
                case SeriesCompareOptionsEnum.socialTopicsWithinTheme:
                    if (option.value.subscriptionIds.length === 0) {
                        this.emptyParent = true;
                        return;
                    }

                    this.buildThemeComparisonSeriesList(option.value as Theme);
                    break;
            }
        } else {
            this.selectedParentOption = null;
        }
        this.emptyParent = false;
    }

    /**
     * Given a TagGroupModel, assign to this component's seriesList property a list mapped from
     * each tag within the TagGroupModel.
     */
    private buildTagGroupComparisonSeriesList(tagGroup: TagGroupModel) {
        const seriesList: ReportSeriesModel[] = [];

        let blockTypeKey = null;

        if (this.block && this.block.type && this.block.type.key) {
            blockTypeKey = this.block.type.key;
        }

        // use only up to whatever the maximum number of series is
        const range: number = Math.min(tagGroup.tagIds.length, this.field.maxNumberOfSeries);
        const tagIds = tagGroup.tagIds.slice(0, range);

        this.tagsService.getTagsByIds(tagIds).subscribe(tags => {
            tags.forEach(tag => {
                const seriesItem = new ReportSeriesModel(null, blockTypeKey, SeriesTypeEnum.tag);
                seriesItem.mentions.tagId = tag.id;
                seriesItem.label = tag.name;
                seriesItem.styles = new GenericStyleModel();
                seriesItem.styles.parentGroupId = tagGroup.id;

                if (this.seriesList[0]) {
                    seriesItem.dateRange = this.seriesList[0].dateRange;
                }
                this.filterService.clearFilters();
                seriesList.push(seriesItem);
            });

            this.seriesList = seriesList;
            this.changes.emit(this.seriesList);
        });
    }

    /**
     * Given a TagGroupModel, assign to this component's seriesList property a list mapped from
     * each tag within the TagGroupModel.
     */
    private buildThemeComparisonSeriesList(theme: Theme) {
        const seriesList: ReportSeriesModel[] = [];
        const themeAgents = this.themeAgentMap.get(theme.id) || [];

        let blockTypeKey = null;

        if (this.block && this.block.type && this.block.type.key) {
            blockTypeKey = this.block.type.key;
        }

        // use only up to whatever the maximum number of series is
        const range: number = Math.min(themeAgents.length, this.field.maxNumberOfSeries);

        for (let i = 0; i < range; i++) {
            const socialTopicId = themeAgents[i].id;
            const seriesItem = new ReportSeriesModel(null, blockTypeKey, SeriesTypeEnum.socialTopic);

            seriesItem.mentions.socialTopicId = socialTopicId;
            seriesItem.label = themeAgents[i].name;
            seriesItem.styles = new GenericStyleModel();
            seriesItem.styles.parentThemeId = theme.id;

            if (this.seriesList[0]) {
                seriesItem.dateRange = this.seriesList[0].dateRange;
            }
            this.filterService.clearFilters();

            seriesList.push(seriesItem);
        }

        this.seriesList = seriesList;
        this.changes.emit(this.seriesList);
    }

    goToParentSource($event: Event) {
        const searchCriteriaModel = new MentionSearchCriteriaModel();
        let queryString;
        if (this.selectedParentOption) {
            switch (this.selectedCompareOption?.value) {
                case SeriesCompareOptionsEnum.tagsWithinTagGroup:
                    searchCriteriaModel.query.tagGroup.include = [this.selectedParentOption.value.id];
                    searchCriteriaModel.query.dateQuery = DateQueryModel.clone(this.seriesList[0].dateRange);
                    queryString = MentionSearchCriteriaModel.toFilterQueryString(searchCriteriaModel);
                    window.open(`${this.appBaseHref}Monitoring/mentions/saved?filters=${queryString}`);
                    break;
                case SeriesCompareOptionsEnum.socialTopicsWithinTheme:
                    searchCriteriaModel.query.theme.include = [this.selectedParentOption.value.id];
                    searchCriteriaModel.query.dateQuery = DateQueryModel.clone(this.seriesList[0].dateRange);
                    queryString = MentionSearchCriteriaModel.toFilterQueryString(searchCriteriaModel);
                    window.open(`${this.appBaseHref}Social/themes/theme/${this.selectedParentOption.value.id}/postFeed?filters=${queryString}`);
                    break;
            }
        }
        ($event?.currentTarget as HTMLElement)?.blur();
    }

    ngOnDestroy() {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
}
